import { cn } from '@/lib/utils'
import { Inline } from './row'
import { HERO_LAYOUT, ANSWERS_LAYOUT, BENTO_ROW, BENTO_SPAN, OPERATIONS_SUPPORT_LAYOUT } from './card-layout'
import { Figure } from './bento'
import { placed } from '@/lib/style-guard'

/* The same block as `Skeleton` in loader.tsx, kept local so loader can use these shapes without
   a circular import. */
function Skeleton({ className, shape = 'default' }: { className?: string; shape?: 'default' | 'lg' | 'xl' }) {
  return <div className={cn('skeleton-blink bg-[var(--surface-muted)]', shape === 'xl' ? 'rounded-[var(--radius-xl)]' : shape === 'lg' ? 'rounded-[var(--radius-lg)]' : 'rounded-[var(--radius-md)]', placed(className, 'Skeleton'))} />
}

/**
 * Loading placeholders in the shape of the cards they stand for (Parley motion, 24.10.2026: the
 * reference's placeholders are the card's own outline, blinking, not a grey slab). The real card
 * then enters in the same place, so nothing jumps. One file, three shapes, used everywhere a
 * Parley card loads; the blink is `skeleton-blink` (tokens in index.css).
 */
export function CardSkeleton({ shape, className, rows = 3 }: { shape: 'hero' | 'figure' | 'rows'; className?: string; rows?: number }) {
  if (shape === 'hero') return (
    <div aria-hidden="true" className={cn('min-w-0 rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-[var(--card-shadow)] sm:p-[22px]', HERO_LAYOUT.card, placed(className, 'CardSkeleton'))}>
      <div className={HERO_LAYOUT.heading}><Skeleton className="h-5 w-2/3" /></div>
      <div className={HERO_LAYOUT.metric}><Skeleton className="h-12 w-40 max-w-full" /></div>
      <div className={HERO_LAYOUT.meta}><Skeleton className="h-4 w-3/4" /></div>
      <Skeleton className={HERO_LAYOUT.meter} />
      <div className={HERO_LAYOUT.legend}><Skeleton className="h-4 w-48 max-w-full" /></div>
      <div className={HERO_LAYOUT.action}><Skeleton className="ml-auto h-5 w-32" /></div>
    </div>
  )
  if (shape === 'figure') return (
    <div aria-hidden="true" className={cn('rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-4 shadow-[var(--card-shadow)] sm:p-5', placed(className, 'CardSkeleton'))}>
      <div className="text-small"><Skeleton className="h-[1lh] w-32 max-w-full" /></div>
      <div className="relative mt-2"><span className="invisible"><Figure size="md">0</Figure></span><Skeleton className="absolute inset-y-0 left-0 w-24" /></div>
      <div className="mt-2 text-small"><Skeleton className="h-[1lh] w-full" /></div>
    </div>
  )
  return (
    <div
      aria-hidden="true"
      className={cn('min-w-0 rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 shadow-[var(--card-shadow)] sm:p-[22px]', placed(className, 'CardSkeleton'))}
    >
      <Skeleton className="h-5 w-32" />
      {shape === 'rows' && <RowsSkeleton rows={rows} className="mt-4" />}
    </div>
  )
}

/**
 * List rows still loading (icon tile, title, one line), for a list that already sits inside a card.
 * The one shape every list placeholder uses, so a tab never blinks grey slabs of its own height.
 */
export function RowsSkeleton({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div aria-hidden="true" className={cn('flex flex-col gap-2', placed(className, 'RowsSkeleton'))}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="rounded-[var(--radius-xl)] bg-[var(--surface-muted)] p-3"><Inline gap="md">
          <Skeleton shape="lg" className="size-10 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-3 w-1/3" />
          </div>
        </Inline></div>
      ))}
    </div>
  )
}

/** The body of an event tab still loading: the overview's hero beside its rows, any other tab one
 *  list card. The same body the page-level `EventSkeleton` draws, so the tab's own placeholder
 *  takes over in place instead of swapping shape (David's recording, 24.10.2026). */
export function EventBodySkeleton({ overview = false, withHeading = true }: { overview?: boolean; withHeading?: boolean }) {
  return overview ? (
    <div aria-hidden="true" className="space-y-5">
      {withHeading && <div><div className="text-h2"><Skeleton className="h-[1lh] w-64 max-w-full" /></div><div className="mt-1 text-small"><Skeleton className="h-[1lh] w-72 max-w-full" /></div></div>}
      <div className={BENTO_ROW}>
        <div className={cn('rounded-[var(--card-radius)] border border-[var(--card-border)] bg-[var(--card-bg)] p-5 sm:p-[22px]', BENTO_SPAN[5], ANSWERS_LAYOUT.card)}>
          <div className={ANSWERS_LAYOUT.heading}><Skeleton className="h-5 w-40" /></div>
          <div className={ANSWERS_LAYOUT.metric}><Skeleton className="h-12 w-40" /></div>
          <Skeleton className={ANSWERS_LAYOUT.meter} />
          <div className={ANSWERS_LAYOUT.legend}><Skeleton className="h-5 w-48" /></div>
        </div>
        <CardSkeleton shape="figure" className={OPERATIONS_SUPPORT_LAYOUT.form} />
        <CardSkeleton shape="figure" className={OPERATIONS_SUPPORT_LAYOUT.placement} />
        <CardSkeleton shape="rows" rows={2} className={BENTO_SPAN[12]} />
      </div>
    </div>
  ) : (
    <CardSkeleton shape="rows" rows={4} />
  )
}

/** An event's workspace still loading: the header (cover, title, doors), the tab track and the
 *  overview's first row, so every step of opening an event shows the same shape (24.10.2026: it
 *  went through three different placeholders and a «Laster…» card with a spinner). */
export function EventSkeleton({ overview = true }: { overview?: boolean }) {
  return (
    <div className="pt-5" aria-hidden="true">
      <Inline gap="md" className="mb-2 flex">
        <Skeleton shape="lg" className="size-12" />
        <Skeleton className="h-8 w-64 max-w-full" />
        {/* Draws a glyph in the title's face while the placeholder shows, so the browser fetches an
            event's display font now and the title does not land in the fallback face and then
            swap (24.10.2026). Clipped like sr-only, but rendered, which is what starts the load. */}
        <span className="sr-only font-hero font-bold">Aa</span>
      </Inline>
      <Inline gap="md" className="mb-4 min-h-11 flex-col items-start justify-between md:flex-row md:items-center"><Skeleton className="h-6 w-36" /><Skeleton className="h-11 w-56" /></Inline>
      <Skeleton shape="xl" className="mb-4 hidden h-13 w-full max-w-xl md:block" />
      <EventBodySkeleton overview={overview} />
    </div>
  )
}

/** A page's own title and one line under it, where `PageShell` will draw them. */
export function TitleSkeleton() {
  return (
    <div aria-hidden="true" className="space-y-2">
      <Skeleton className="h-8 w-56" />
      <Skeleton className="h-4 w-72 max-w-full" />
    </div>
  )
}

/** The dashboard's two stacking columns (DashboardPage draws the same while it reads). */
export function DashboardBodySkeleton() {
  return (
    <div aria-hidden="true" className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
      <div className="flex flex-col gap-4 max-lg:contents lg:col-span-5">
        <CardSkeleton shape="hero" className="max-lg:order-1" />
        <CardSkeleton shape="rows" rows={4} className="max-lg:order-4" />
      </div>
      <div className="flex flex-col gap-4 max-lg:contents lg:col-span-7">
        <CardSkeleton shape="rows" rows={3} className="max-lg:order-2" />
        <CardSkeleton shape="rows" rows={3} className="max-lg:order-3" />
      </div>
    </div>
  )
}

/** A figure row the size of `StatCard`s, so a list page's KPIs do not push the list down. */
export function KpiRowSkeleton({ count = 3 }: { count?: number }) {
  return (
    <div aria-hidden="true" className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {Array.from({ length: count }).map((_, i) => <CardSkeleton key={i} shape="figure" className={i > 0 ? 'max-md:hidden' : undefined} />)}
    </div>
  )
}

/** A list page (`ListShell`): its KPIs, its toolbar and its list, in that order. */
export function ListBodySkeleton({ kpis = true }: { kpis?: boolean }) {
  return (
    <div aria-hidden="true" className="space-y-6">
      {kpis && <KpiRowSkeleton />}
      <Skeleton shape="xl" className="h-11 w-full max-w-md" />
      <CardSkeleton shape="rows" rows={6} />
    </div>
  )
}

/** The event editor: its section list beside the section, the way `BuilderShell` lays them out. */
export function EditorBodySkeleton() {
  return (
    <div aria-hidden="true" className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)]">
      <CardSkeleton shape="rows" rows={6} />
      <CardSkeleton shape="rows" rows={4} />
    </div>
  )
}

/** Settings: the menu rail beside the open panel. */
export function SettingsBodySkeleton() {
  return (
    <div aria-hidden="true" className="flex flex-col gap-6 lg:flex-row lg:gap-8">
      <CardSkeleton shape="rows" rows={6} className="hidden lg:block lg:w-[248px] lg:shrink-0" />
      <CardSkeleton shape="rows" rows={4} className="min-w-0 flex-1" />
    </div>
  )
}

/** A participant's view of an event: the cover, the facts row and the title, then the tab body. */
export function ParticipantEventSkeleton() {
  return (
    <div className="space-y-5" aria-hidden="true">
      <Skeleton shape="xl" className="h-48 w-full sm:h-64" />
      <Inline gap="sm" className="flex">
        <Skeleton shape="lg" className="h-8 w-28" />
        <Skeleton shape="lg" className="h-8 w-32" />
        <Skeleton shape="lg" className="h-8 w-40" />
      </Inline>
      <Skeleton className="h-10 w-80 max-w-full" />
      <Skeleton shape="xl" className="h-11 w-full max-w-md" />
      <CardSkeleton shape="rows" rows={4} />
    </div>
  )
}

/** The calendar: the agenda list beside its side column (follow-up, feed). */
export function CalendarBodySkeleton() {
  return (
    <div aria-hidden="true" className="grid gap-6 lg:grid-cols-[1fr_var(--sidebar-secondary-width)]">
      <CardSkeleton shape="rows" rows={5} />
      <div className="space-y-4">
        <CardSkeleton shape="rows" rows={1} />
        <CardSkeleton shape="rows" rows={2} />
      </div>
    </div>
  )
}

/** A page that opens on a row of tabs or filter chips, then a list (Kommunikasjon, Varsler). */
export function TabbedListBodySkeleton() {
  return (
    <div aria-hidden="true" className="space-y-6">
      <Skeleton shape="xl" className="h-11 w-full max-w-lg" />
      <CardSkeleton shape="rows" rows={5} />
    </div>
  )
}

/** Meldinger: the conversation list on the left, the open thread's empty pane on the right. */
export function MessagesSkeleton() {
  return (
    <div aria-hidden="true" className="flex h-full min-h-0 flex-1">
      <div className="w-full border-r border-[var(--border-default)] p-3.5 md:w-80">
        <Skeleton shape="xl" className="mb-4 h-11 w-full" />
        <RowsSkeleton rows={4} />
      </div>
      <div className="hidden flex-1 md:block" />
    </div>
  )
}
